import { FC, SyntheticEvent, useState } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import omit from 'lodash/omit';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import ClearIcon from '@mui/icons-material/Cancel';

import * as K from 'common/constants';
import { clearGraphQLErrorMessage } from 'common/util/string';
import ELEMENT from 'common/element';
import { getDateRangeText, getFormattedTimeOffsetUTC, getTimezoneOffset } from 'common/util/time';
import { svgStringToBase64PNG } from 'common/util/images';

import Dialog from 'components/common/Dialog/Dialog';
import { currentChartRefState } from 'components/common/Graphs/graphs.state';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import ResizableTextArea from 'components/common/Input/ResizableTextArea';
import Dropdown from 'components/common/Dropdown/Dropdown';
import {
  absoluteResolvedUrlState,
  draftSearchParamState,
  queryTimeRangeState,
  searchParamRecordState,
} from 'components/common/Managers/RouteStateManager.state';

import { DELETE_SLACK_CHANNEL, GET_SLACK_CHANNELS, UPLOAD_IMAGE_TO_SLACK } from './queries';

import './PostGraphToSlackDialog.scss';

const SLACK_CHART_WIDTH = 1200;
const SLACK_CHART_HEIGHT = 488;
const MAX_CHAR_ALLOWED = 1000;
const DEFAULT_FILE_NAME = K.PRODUCT_NAME + '_Graph';
const DEFAULT_FILE_TYPE = 'png';

/**
 *  Construct a Slack initial comment which contains four items in this order.
 *
 *  example:
 *  *Search query:* `foo query`
 *  *Date range:* Sep 08 , 10:19:03 - 10:19:33 PM [UTC-7]
 *  foo message
 *  <http://localhost:8080/events?mode=graph&teamToken=pVJVKcneYLb8pllNGrYrJw--&endTime=1599619521694&startTime=1599612321694|View in Scalyr>
 *
 */
const constructInitialComment = (searchQuery: string, dateRangeText: string, slackMessage: string, viewInScalyrLink: string): string => {
  let message = '*Search query:* `' + searchQuery + '`\n';
  message += '*Date range:* ' + dateRangeText + '\n';
  if (slackMessage) {
    message += slackMessage + '\n';
  }
  message += '<' + viewInScalyrLink + '|View in ' + K.PRODUCT_NAME + '>';
  return message;
};

type PostGraphToSlackDialogProps = {
  dialogOpened: boolean;
  setDialogOpened: (value: boolean) => void;
};

type SlackChanel = {
  name: string;
  id: string;
};
/**
 *
 * A dialog that performing posting graph to slack
 *
 */
const PostGraphToSlackDialog: FC<PostGraphToSlackDialogProps> = ({
  dialogOpened,
  setDialogOpened,
}: PostGraphToSlackDialogProps) => {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const timezone = useRecoilValue(timezoneState);
  const timezoneOffset = getTimezoneOffset(timezone);
  const showErrorAlert = (message: string): void => {
    setPageAlertConfig({ message, variant: PageAlertVariant.ERROR, autoHide: true });
  };
  const showSuccessAlert = (message: string): void => {
    setPageAlertConfig({ message, variant: PageAlertVariant.SUCCESS, autoHide: true });
  };
  const draftParams = useRecoilValue(draftSearchParamState);
  const searchQuery = draftParams[K.URL_PARAM.FILTER];
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const absoluteUrl = useRecoilValue(absoluteResolvedUrlState);
  const searchParams = useRecoilValue(searchParamRecordState);
  const [input, setInput] = useState<string>('');
  const [inputValid, setInputValid] = useState<boolean>(true);
  const [selectedChannel, setSelectedChannel] = useState<SlackChanel | null>(null);
  const { data, refetch } = useQuery(GET_SLACK_CHANNELS, {
    onCompleted: (response) => {
      setSelectedChannel(response?.slackChannels?.channels?.[0] || null);
    },
    onError: (error: ApolloError) => {
      showErrorAlert(error.message);
    },
  });

  const [uploadImageToSlack] = useMutation(UPLOAD_IMAGE_TO_SLACK, {
    onCompleted: () => {
      showSuccessAlert('Graph has been posted to Slack');
      refetch();
    },
    onError: (error: ApolloError) => {
      showErrorAlert(clearGraphQLErrorMessage(error.message));
    },
  });
  const [deleteSlackChannel] = useMutation(DELETE_SLACK_CHANNEL, {
    onCompleted: () => {
      showSuccessAlert('Channel deleted successfully');
    },
    onError: (error: ApolloError) => {
      showErrorAlert(error.message);
    },
  });
  const chartRef = useRecoilValue(currentChartRefState); // The ref to the current chart instance
  const chart = chartRef?.current?.chart;

  const onDialogSubmit = (): void => {
    let title = K.DEFAULT_EXPORT_GRAPH_TITLE;
    const subtitle = !searchParams.facet && searchParams.breakdownFacet ? ` broken down by ${searchParams.breakdownFacet}` : '';
    title += subtitle;
    if (searchParams.facet) {
      title = `Graph of ${searchParams.facet}`;
    }
    if (chart) {
      chart.setTitle({
        text: title,
        style: {
          display: 'inline',
          'font-weight': 'bold',
        },
      });
      chart.legend.update({ enabled: true }, false);
      const svgString = chart.getSVG({ chart: { width: SLACK_CHART_WIDTH, height: SLACK_CHART_HEIGHT } });

      const graphSearchQuery = searchQuery || 'none';
      const dateRangeText = getDateRangeText(Number(queryTimeRange.startTime), Number(queryTimeRange.endTime), timezone, true)
        .replace('·', ',') + ' [' + getFormattedTimeOffsetUTC(timezoneOffset) + ']';
      const slackMessage = input;
      const viewInScalyrLink = searchParams.endTime ? absoluteUrl : `${absoluteUrl}&endTime=${queryTimeRange.endTime}`;
      const initialComment = constructInitialComment(graphSearchQuery, dateRangeText, slackMessage, viewInScalyrLink);

      const imageCallback = (pngData: string): void => {
        uploadImageToSlack({
          variables: {
            channelIds: [selectedChannel?.id],
            image: {
              base64Image: pngData,
              fileName: DEFAULT_FILE_NAME,
              fileType: DEFAULT_FILE_TYPE,
            },
            initialComment,
          },
        });
      };
      svgStringToBase64PNG(SLACK_CHART_WIDTH, SLACK_CHART_HEIGHT, svgString, imageCallback);
      chart.setTitle({
        text: '',
        style: {
          display: 'none',
        },
      });
      chart.legend.update({ enabled: false });
    }
  };

  const onInputChange = (e: SyntheticEvent): void => {
    const inputValue = (e?.target as HTMLTextAreaElement)?.value;
    setInput(inputValue);
    setInputValid(inputValue.length <= MAX_CHAR_ALLOWED);
  };

  const onClose = (): void => {
    setDialogOpened(false);
    setInput('');
  };

  const channelsSection = data?.slackChannels?.channels?.length > 0 ? data?.slackChannels?.channels.map((channel: SlackChanel) => ({
    text: channel?.name,
    clickHandler: (): void => {
      setSelectedChannel(channel);
    },
    children: (
      <div
        className={`PostGraphToSlackDialog__SlackChannelContainer ${
          channel.id === selectedChannel?.id ? 'PostGraphToSlackDialog__SlackChannelContainer--Selected' : ''
        }`}
      >
        <div>{channel?.name}</div>
        <div>
          <ClearIcon
            onClick={(e: SyntheticEvent): void => {
              e.stopPropagation();
              deleteSlackChannel({
                variables: {
                  channelInfo: omit(channel, '__typename'),
                },
              });
            }}
          />
        </div>
      </div>
    ),
  })) : [];

  const sections = [
    [
      {
        text: 'Add Slack Channel',
        clickHandler: (): void => {
          const extraParams  = searchParams[K.URL_PARAM.POST_GRAPH_TO_SLACK_OPEN] === 'true' ? '' : '&postGraphToSlackOpen=true';
          const url = `${data?.slackAuthApi}?client_id=${data?.slackClientId}&scope=incoming-webhook&user_scope=files:write&state=${encodeURIComponent(window.location.href + extraParams)}&redirect_uri=${window.location.protocol}//${window.location.host}/slackoauth`;
          window.location.href = url;
        },
      },
    ],
    channelsSection,
  ];

  const okButtonEnabled = !!data?.slackChannels?.userName && !!selectedChannel && inputValid;
  return (
    <Dialog
      title="Post Graph To Slack"
      className="PostGraphToSlackDialog"
      show={dialogOpened}
      close={onClose}
      okCallback={onDialogSubmit}
      okButtonDisabled={!okButtonEnabled}
      okButtonText="Post graph"
      testId={ELEMENT.postToSlackDialog}
    >
      <div className="PostGraphToSlackDialog__Description">
        <strong>Message </strong>
        (optional)
      </div>
      <ResizableTextArea
        isInvalid={!inputValid}
        value={input}
        onChange={onInputChange}
        minRows={6}
        placeholder="Add a message to include"
        testId={ELEMENT.postToSlackDialogInput}
      />
      <div
        className={`PostGraphToSlackDialog__SlackData ${
          inputValid ? '' : 'PostGraphToSlackDialog__SlackData--invalid'
        }`}
      >
        <div />
        <div>{`${input.length} / ${MAX_CHAR_ALLOWED}`}</div>
      </div>
      <div className="PostGraphToSlackDialog__SlackData">
        <Dropdown sections={sections} itemAs="button" size="med">
          {selectedChannel?.name || 'No authorized channels'}
        </Dropdown>
        {data?.slackChannels?.userName && (
          <div>
            <strong>Posting as: </strong>
            {data.slackChannels.userName}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default PostGraphToSlackDialog;
